import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { io } from 'socket.io-client';
import axios from 'axios';
import {
  ShieldAlert, Wifi, WifiOff, Volume2, VolumeX, RefreshCw,
  CheckCircle2, MapPin, Clock, User, Battery, Copy, ExternalLink,
} from 'lucide-react';
import { API, BACKEND_URL, SOCKET_IO_PATH } from '../../utils/apiConfig';

const getHeaders = () => {
  const t = localStorage.getItem('token');
  return { Authorization: `Bearer ${t}` };
};

// ── Alarm-Beep generator (no audio file dependency) ───────────────
function playAlarmChime(volume = 0.4) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    // 3-tone urgent pattern: 880Hz → 660Hz → 880Hz over 0.6s
    const notes = [{ f: 880, t: 0 }, { f: 660, t: 0.2 }, { f: 880, t: 0.4 }];
    notes.forEach((n) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = n.f;
      gain.gain.setValueAtTime(volume, ctx.currentTime + n.t);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + n.t + 0.18);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + n.t);
      osc.stop(ctx.currentTime + n.t + 0.2);
    });
    setTimeout(() => ctx.close(), 1000);
  } catch {}
}

export const AdminSOSDashboardTab = () => {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState([]);
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(null);
  const [muted, setMuted] = useState(false);
  const [flashCount, setFlashCount] = useState(0); // re-trigger flash on each new alert
  const socketRef = useRef(null);
  const mutedRef = useRef(false);

  useEffect(() => { mutedRef.current = muted; }, [muted]);

  // ── Bootstrap: REST-load + socket connect ──────────────────────
  const loadInitial = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/safety/admin/sos-alerts`,
                                  { headers: getHeaders() });
      const list = res.data?.alerts || res.data || [];
      // Show open + acknowledged (active states), most recent first
      const active = (Array.isArray(list) ? list : [])
        .filter(a => ['open', 'acknowledged'].includes(a.status))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAlerts(active);
    } catch (e) {
      // graceful — empty list is fine
    }
  }, []);

  useEffect(() => {
    loadInitial();
    const token = localStorage.getItem('token');
    const path = SOCKET_IO_PATH || '/api/socket.io';
    const url  = BACKEND_URL || window.location.origin;

    const socket = io(url, {
      path,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1500,
      reconnectionDelayMax: 8000,
      reconnectionAttempts: Infinity,
      auth: { token },
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('sos:subscribe', { token });
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('sos:subscribed', (d) => {
      if (d?.status !== 'ok') {
        // Backend rejected — likely not admin. We surface this as disconnect.
        setConnected(false);
      }
    });

    socket.on('sos:new', (alert) => {
      // dedupe
      setAlerts(prev => {
        if (prev.some(a => a.id === alert.id)) return prev;
        return [{ ...alert, status: alert.status || 'open' }, ...prev];
      });
      setFlashCount(c => c + 1);
      if (!mutedRef.current) playAlarmChime();
    });
    socket.on('sos:ack', (evt) => {
      setAlerts(prev => prev.map(a =>
        a.id === evt.id ? { ...a, status: evt.status, ack_by: evt.ack_by } : a
      ));
    });
    socket.on('sos:resolved', (evt) => {
      setAlerts(prev => prev.filter(a => a.id !== evt.id));
    });

    return () => {
      try { socket.emit('sos:unsubscribe', {}); } catch {}
      socket.disconnect();
      socketRef.current = null;
    };
  }, [loadInitial]);

  // ── Acknowledge ───────────────────────────────────────────────
  const acknowledge = async (alertId) => {
    setBusy(alertId);
    try {
      await axios.post(
        `${API}/safety/admin/sos/${alertId}/acknowledge`,
        {},
        { headers: getHeaders() }
      );
      setAlerts(prev => prev.map(a =>
        a.id === alertId ? { ...a, status: 'acknowledged' } : a
      ));
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  const copyCoords = (a) => {
    const txt = `${a.lat},${a.lng}`;
    navigator.clipboard?.writeText(txt).catch(() => {});
  };

  const openOnMap = (a) => {
    if (a.lat == null || a.lng == null) return;
    const url = `https://maps.google.com/?q=${a.lat},${a.lng}`;
    window.open(url, '_blank', 'noopener');
  };

  const openCount = alerts.filter(a => a.status === 'open').length;

  return (
    <div data-testid="admin-sos-dashboard-tab">
      {/* ── Header bar ─────────────────────────────────────── */}
      <div style={s.headerBar} data-testid="sos-dashboard-header">
        <div style={s.titleWrap}>
          <ShieldAlert size={22} color={openCount > 0 ? '#ef4444' : '#94a3b8'}
            style={openCount > 0 ? s.iconPulse : {}} />
          <div>
            <h2 style={s.title}>{t('sosDashboard.title', 'SOS-Leitstand')}</h2>
            <div style={s.subtitle}>
              <span style={{
                ...s.connDot,
                background: connected ? '#22c55e' : '#ef4444',
              }} />
              <span style={s.connText}>
                {connected ? t('sosDashboard.live', 'Live') : t('sosDashboard.reconnecting', 'Verbindung wird wiederhergestellt…')}
              </span>
            </div>
          </div>
        </div>
        <div style={s.headerActions}>
          <button
            data-testid="sos-dashboard-mute"
            style={s.iconBtn}
            onClick={() => setMuted(m => !m)}
            title={muted ? t('sosDashboard.muted', 'Stumm') : t('sosDashboard.sound', 'Ton')}
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <button
            data-testid="sos-dashboard-refresh"
            style={s.iconBtn}
            onClick={loadInitial}
            title={t('common.refresh', 'Aktualisieren')}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* ── Stats row ──────────────────────────────────────── */}
      <div style={s.statsRow}>
        <div style={{
          ...s.statCard,
          borderColor: openCount > 0 ? '#ef4444' : 'rgba(148,163,184,0.22)',
          background: openCount > 0
            ? 'rgba(239,68,68,0.08)'
            : 'rgba(15,23,42,0.6)',
        }}>
          <div style={{ ...s.statValue, color: openCount > 0 ? '#fca5a5' : '#cbd5e1' }}>
            {openCount}
          </div>
          <div style={s.statLabel}>{t('sosDashboard.active', 'Aktive Notrufe')}</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statValue}>
            {alerts.filter(a => a.status === 'acknowledged').length}
          </div>
          <div style={s.statLabel}>{t('sosDashboard.statusAcknowledged', 'Übernommen')}</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statValue}>
            {connected ? <Wifi size={26} color="#22c55e" /> : <WifiOff size={26} color="#ef4444" />}
          </div>
          <div style={s.statLabel}>WebSocket</div>
        </div>
      </div>

      {/* ── Alerts list ────────────────────────────────────── */}
      {alerts.length === 0 ? (
        <div style={s.emptyCard} data-testid="sos-dashboard-empty">
          <CheckCircle2 size={32} color="#22c55e" />
          <div style={{ marginTop: 10, fontSize: 14 }}>
            {t('sosDashboard.noActive', 'Keine aktiven Notrufe — alles ruhig.')}
          </div>
        </div>
      ) : (
        <div data-testid="sos-dashboard-alerts">
          {alerts.map((alert) => (
            <AlertCard
              key={alert.id + '-' + flashCount}
              alert={alert}
              busy={busy === alert.id}
              onAcknowledge={() => acknowledge(alert.id)}
              onCopy={() => copyCoords(alert)}
              onMap={() => openOnMap(alert)}
              t={t}
            />
          ))}
        </div>
      )}

      <style>{`
        @keyframes sosFlash {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.65); }
          50%      { box-shadow: 0 0 0 16px rgba(239,68,68,0); }
        }
        @keyframes sosIconPulse {
          0%, 100% { transform: scale(1);   opacity: 1; }
          50%      { transform: scale(1.15); opacity: 0.8; }
        }
        .sos-alert-card-open {
          animation: sosFlash 1.4s infinite;
        }
      `}</style>
    </div>
  );
};

// ── Sub-component: Alert Card ────────────────────────────────────
function AlertCard({ alert, busy, onAcknowledge, onCopy, onMap, t }) {
  const isOpen = alert.status === 'open';
  const created = alert.timestamp || alert.created_at;
  const batteryPct = alert.battery_level;
  return (
    <div
      className={isOpen ? 'sos-alert-card-open' : ''}
      data-testid={`sos-alert-${alert.id}`}
      style={{
        ...s.alertCard,
        background: isOpen ? 'rgba(239,68,68,0.12)' : 'rgba(15,23,42,0.6)',
        borderColor: isOpen ? 'rgba(239,68,68,0.55)' : 'rgba(148,163,184,0.2)',
      }}
    >
      <div style={s.alertHead}>
        <div style={s.alertHeadLeft}>
          <ShieldAlert size={18} color={isOpen ? '#ef4444' : '#94a3b8'} />
          <span style={s.alertStatus}>
            {isOpen
              ? t('sosDashboard.statusOpen', 'Offen').toUpperCase()
              : t('sosDashboard.statusAcknowledged', 'Übernommen').toUpperCase()}
          </span>
        </div>
        <div style={s.alertMeta}>
          <Clock size={12} />
          <span>{created ? new Date(created).toLocaleTimeString() : '—'}</span>
        </div>
      </div>

      <div style={s.alertGrid}>
        <div>
          <div style={s.fieldLabel}><User size={11} /> {t('sosDashboard.user', 'User')}</div>
          <div style={s.fieldVal}>{alert.user_name || alert.user_id?.slice(0, 8) || '—'}</div>
          {alert.user_phone && <div style={s.fieldSub}>📞 {alert.user_phone}</div>}
        </div>
        <div>
          <div style={s.fieldLabel}><MapPin size={11} /> {t('sosDashboard.location', 'Standort')}</div>
          <div style={s.fieldVal}>
            {alert.lat != null && alert.lng != null
              ? `${Number(alert.lat).toFixed(4)}, ${Number(alert.lng).toFixed(4)}`
              : '—'}
          </div>
          {alert.accuracy && <div style={s.fieldSub}>±{Math.round(alert.accuracy)}m</div>}
        </div>
        <div>
          <div style={s.fieldLabel}><Battery size={11} /> {t('sosDashboard.battery', 'Akku')}</div>
          <div style={{ ...s.fieldVal,
                         color: batteryPct != null && batteryPct < 20 ? '#fca5a5' : '#e2e8f0' }}>
            {batteryPct != null ? `${batteryPct}%` : '—'}
          </div>
        </div>
      </div>

      {alert.note && (
        <div style={s.alertNote}>"{alert.note}"</div>
      )}

      <div style={s.alertActions}>
        {alert.lat != null && (
          <>
            <button
              data-testid={`sos-alert-copy-${alert.id}`}
              style={s.secondaryBtn} onClick={onCopy}
            >
              <Copy size={13} /> {t('sosDashboard.copyCoords', 'Koordinaten kopieren')}
            </button>
            <button
              data-testid={`sos-alert-map-${alert.id}`}
              style={s.secondaryBtn} onClick={onMap}
            >
              <ExternalLink size={13} /> {t('sosDashboard.viewMap', 'Auf Karte')}
            </button>
          </>
        )}
        {isOpen && (
          <button
            data-testid={`sos-alert-ack-${alert.id}`}
            style={s.ackBtn}
            onClick={onAcknowledge}
            disabled={busy}
          >
            <CheckCircle2 size={15} />
            {busy
              ? t('common.loading', 'Lädt…')
              : t('sosDashboard.acknowledge', 'Einsatz übernehmen')}
          </button>
        )}
      </div>
    </div>
  );
}

const s = {
  headerBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(22px)',
    border: '1px solid rgba(148,163,184,0.18)', borderRadius: 16,
    padding: '14px 18px', marginBottom: 16, color: '#e2e8f0',
  },
  titleWrap: { display: 'flex', alignItems: 'center', gap: 14 },
  title: { fontSize: 20, margin: 0, fontWeight: 700 },
  subtitle: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 12 },
  connDot: { width: 8, height: 8, borderRadius: '50%' },
  connText: { color: '#94a3b8' },
  headerActions: { display: 'flex', gap: 8 },
  iconBtn: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(148,163,184,0.18)',
    color: '#e2e8f0', borderRadius: 10, padding: 8, cursor: 'pointer',
  },
  iconPulse: { animation: 'sosIconPulse 1.2s infinite ease-in-out' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 },
  statCard: {
    background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.22)',
    borderRadius: 14, padding: 16, textAlign: 'center', color: '#e2e8f0',
  },
  statValue: { fontSize: 30, fontWeight: 700, lineHeight: 1 },
  statLabel: { fontSize: 11, opacity: 0.65, marginTop: 6,
               textTransform: 'uppercase', letterSpacing: 0.6 },
  emptyCard: {
    background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.18)',
    borderRadius: 16, padding: 40, textAlign: 'center', color: '#cbd5e1',
  },
  alertCard: {
    border: '1px solid', borderRadius: 16, padding: 18, marginBottom: 14,
    color: '#e2e8f0',
  },
  alertHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  alertHeadLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  alertStatus: { fontSize: 11, fontWeight: 700, letterSpacing: 0.6 },
  alertMeta: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, opacity: 0.7 },
  alertGrid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 12 },
  fieldLabel: { fontSize: 10, opacity: 0.6, textTransform: 'uppercase',
                letterSpacing: 0.5, display: 'inline-flex', alignItems: 'center', gap: 4 },
  fieldVal: { fontSize: 14, fontWeight: 600, marginTop: 4 },
  fieldSub: { fontSize: 11, opacity: 0.5, marginTop: 2 },
  alertNote: {
    fontSize: 13, fontStyle: 'italic', padding: '8px 12px',
    background: 'rgba(255,255,255,0.04)', borderRadius: 8, color: '#cbd5e1',
    marginBottom: 12,
  },
  alertActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  secondaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: 'rgba(255,255,255,0.06)', color: '#cbd5e1',
    border: '1px solid rgba(148,163,184,0.22)', padding: '7px 12px',
    borderRadius: 8, fontSize: 12, cursor: 'pointer',
  },
  ackBtn: {
    marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'linear-gradient(135deg,#ef4444,#dc2626)',
    color: '#fff', border: 'none', padding: '10px 18px',
    borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(239,68,68,0.35)',
  },
};
